import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { createElement } from 'react';
import { trpc } from '../api/trpc';
import { wsManager } from '../api/websocket';
import { getMsalRedirectResult } from '../auth/msalConfig';
import { onUnauthorized } from '../api/auth-events';

interface AuthState {
  token: string | null;
  userId: string | null;
  tenantId: string | null;
}

interface UserProfile {
  email: string;
  name: string;
  isAdmin: boolean;
  workRoles: string[];
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isInitializing: boolean;
  token: string | null;
  userId: string | null;
  tenantId: string | null;
  email: string | null;
  name: string | null;
  isAdmin: boolean;
  workRoles: string[];
  // `login` may return a discriminated result: a 'session' kind for users
  // without MFA enrolled (auth context is hydrated and the caller routes
  // to '/'); a 'challenge' kind for users with MFA enrolled (auth context
  // is NOT hydrated; the caller routes to '/login/mfa' with the
  // challengeToken in state).
  login: (email: string, password: string) => Promise<
    | { kind: 'session' }
    | { kind: 'challenge'; challengeToken: string }
  >;
  loginWithSso: (accessToken: string) => Promise<void>;
  loginWithOktaCallback: (args: { code: string; state: string; tenantSlug?: string | undefined }) => Promise<void>;
  // Hydrate the auth state from a session token issued out-of-band (e.g. by
  // the /register flow's acceptInvitation mutation, which returns a session
  // directly without going through `login`).
  hydrate: (result: { token: string; userId: string; tenantId: string }) => void;
  logout: () => Promise<void>;
  isLoggingIn: boolean;
  loginError: string | null;
  ssoError: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Cache the handoff code at module level so React StrictMode double-mounting
// doesn't lose it after the first mount cleans the URL.
const _handoffCode = new URLSearchParams(window.location.search).get('handoff');
// Cache the redemption promise so the one-time code isn't consumed twice.
let _handoffPromise: Promise<{ token: string; userId: string; tenantId: string }> | null = null;

function getStoredAuth(): AuthState {
  return {
    token: localStorage.getItem('cade_token'),
    userId: localStorage.getItem('cade_userId'),
    tenantId: localStorage.getItem('cade_tenantId'),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>(getStoredAuth);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(() => {
    const stored = getStoredAuth();
    if (stored.token) return false;
    // Initialize if we have a handoff code or an MSAL redirect pending
    return !!_handoffCode || !!getMsalRedirectResult();
  });
  const loginMutation = trpc.auth.login.useMutation();
  const logoutMutation = trpc.auth.logout.useMutation();

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: !!auth.token,
    retry: false,
  });

  useEffect(() => {
    if (meQuery.data) {
      setProfile({
        email: meQuery.data.email,
        name: meQuery.data.name,
        isAdmin: meQuery.data.isAdmin,
        workRoles: meQuery.data.workRoles,
      });
    }
  }, [meQuery.data]);

  // Reconnect WebSocket on mount if already authenticated (e.g. page refresh)
  useEffect(() => {
    if (auth.token && auth.userId && auth.tenantId) {
      wsManager.connect(auth.userId, auth.tenantId, auth.token);
    }
  }, []);

  // Auto-logout when the server rejects our token (expired / revoked)
  useEffect(() => {
    return onUnauthorized(() => {
      localStorage.removeItem('cade_token');
      localStorage.removeItem('cade_userId');
      localStorage.removeItem('cade_tenantId');
      wsManager.disconnect();
      setProfile(null);
      setAuth({ token: null, userId: null, tenantId: null });
    });
  }, []);

  // Redeem a one-time handoff code from the browser extension (or any cross-surface link)
  const redeemHandoffMutation = trpc.auth.redeemHandoff.useMutation();
  const ssoLoginMutation = trpc.auth.ssoLogin.useMutation();

  useEffect(() => {
    if (auth.token) {
      setIsInitializing(false);
      return;
    }

    let cancelled = false;
    async function initAuth() {
      // 1. Check for handoff code (cached at module level to survive StrictMode double-mount)
      if (_handoffCode) {
        // Clean the URL once
        const params = new URLSearchParams(window.location.search);
        params.delete('handoff');
        const cleanUrl = params.toString()
          ? `${window.location.pathname}?${params}`
          : window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);

        try {
          if (!_handoffPromise) {
            _handoffPromise = redeemHandoffMutation.mutateAsync({ code: _handoffCode });
          }
          const result = await _handoffPromise;
          if (!cancelled) {
            localStorage.setItem('cade_token', result.token);
            localStorage.setItem('cade_userId', result.userId);
            localStorage.setItem('cade_tenantId', result.tenantId);
            setAuth({ token: result.token, userId: result.userId, tenantId: result.tenantId });
            wsManager.connect(result.userId, result.tenantId, result.token);
          }
          return;
        } catch {
          // Handoff expired or invalid — fall through to MSAL / login
        }
      }

      // 2. Handle MSAL redirect response (SSO login flow)
      // Use the module-level cached promise so React StrictMode double-mounting
      // doesn't cause handleRedirectPromise() to be called twice (second call
      // returns null because the redirect response was already consumed).
      const redirectPromise = getMsalRedirectResult();
      if (redirectPromise) {
        try {
          const result = await redirectPromise;
          const ssoToken = result?.idToken || result?.accessToken;
          if (ssoToken && !cancelled) {
            const ssoResult = await ssoLoginMutation.mutateAsync({ token: ssoToken });
            if (cancelled) return;
            // Discriminated response: authenticated → hydrate session;
            // request_created → user's Entra tenant has no IdpConfig and
            // their SSO attempt was routed into the SignupRequest approval
            // queue. Spec: docs/spec/topics/auth/ms-sso-signup-request.md.
            if (ssoResult.status === 'request_created') {
              // We're outside the Router context here; window.location is the
              // simplest way to land on /login/access-requested without
              // racing the not-yet-mounted Routes tree.
              const params = new URLSearchParams({
                e: ssoResult.email,
                p: ssoResult.alreadyPending ? '1' : '0',
              });
              window.location.replace(`/login/access-requested?${params}`);
              return;
            }
            localStorage.setItem('cade_token', ssoResult.token);
            localStorage.setItem('cade_userId', ssoResult.userId);
            localStorage.setItem('cade_tenantId', ssoResult.tenantId);
            setAuth({ token: ssoResult.token, userId: ssoResult.userId, tenantId: ssoResult.tenantId });
            wsManager.connect(ssoResult.userId, ssoResult.tenantId, ssoResult.token);
          } else if (result && !ssoToken) {
            const msg = 'SSO returned no token — check Entra app registration scopes and SPA redirect URI config';
            // eslint-disable-next-line no-console
            console.error('[SSO]', msg);
            if (!cancelled) setSsoError(msg);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[SSO] redirect handling failed:', err);
          if (!cancelled) setSsoError(err instanceof Error ? err.message : 'SSO login failed');
        }
      }
    }

    void initAuth().finally(() => {
      if (!cancelled) setIsInitializing(false);
    });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<
      | { kind: 'session' }
      | { kind: 'challenge'; challengeToken: string }
    > => {
      const result = await loginMutation.mutateAsync({ email, password });
      if (result.kind === 'challenge') {
        return { kind: 'challenge', challengeToken: result.challengeToken };
      }
      localStorage.setItem('cade_token', result.token);
      localStorage.setItem('cade_userId', result.userId);
      localStorage.setItem('cade_tenantId', result.tenantId);
      setAuth({ token: result.token, userId: result.userId, tenantId: result.tenantId });
      wsManager.connect(result.userId, result.tenantId, result.token);
      return { kind: 'session' };
    },
    [loginMutation],
  );

  const hydrate = useCallback(
    (result: { token: string; userId: string; tenantId: string }) => {
      localStorage.setItem('cade_token', result.token);
      localStorage.setItem('cade_userId', result.userId);
      localStorage.setItem('cade_tenantId', result.tenantId);
      setAuth({ token: result.token, userId: result.userId, tenantId: result.tenantId });
      wsManager.connect(result.userId, result.tenantId, result.token);
    },
    [],
  );

  const loginWithSso = useCallback(
    async (accessToken: string) => {
      const result = await ssoLoginMutation.mutateAsync({ token: accessToken });
      if (result.status === 'request_created') {
        // Unknown-Entra-tenant path. Send the caller (LoginPage / extension)
        // to the access-requested screen via the same URL the MSAL handler
        // uses; preserves UX consistency.
        const params = new URLSearchParams({
          e: result.email,
          p: result.alreadyPending ? '1' : '0',
        });
        window.location.replace(`/login/access-requested?${params}`);
        return;
      }
      localStorage.setItem('cade_token', result.token);
      localStorage.setItem('cade_userId', result.userId);
      localStorage.setItem('cade_tenantId', result.tenantId);
      setAuth({ token: result.token, userId: result.userId, tenantId: result.tenantId });
      wsManager.connect(result.userId, result.tenantId, result.token);
    },
    [ssoLoginMutation],
  );

  const oktaCallbackMutation = trpc.auth.oktaCallback.useMutation();
  const loginWithOktaCallback = useCallback(
    async (args: { code: string; state: string; tenantSlug?: string | undefined }) => {
      const result = await oktaCallbackMutation.mutateAsync(args);
      localStorage.setItem('cade_token', result.token);
      localStorage.setItem('cade_userId', result.userId);
      localStorage.setItem('cade_tenantId', result.tenantId);
      setAuth({ token: result.token, userId: result.userId, tenantId: result.tenantId });
      wsManager.connect(result.userId, result.tenantId, result.token);
    },
    [oktaCallbackMutation],
  );

  const logout = useCallback(async () => {
    if (auth.token) {
      await logoutMutation.mutateAsync({ token: auth.token }).catch(() => {});
    }
    localStorage.removeItem('cade_token');
    localStorage.removeItem('cade_userId');
    localStorage.removeItem('cade_tenantId');
    wsManager.disconnect();
    setProfile(null);
    setAuth({ token: null, userId: null, tenantId: null });
  }, [auth.token, logoutMutation]);

  const value: AuthContextValue = {
    isAuthenticated: !!auth.token,
    isInitializing,
    token: auth.token,
    userId: auth.userId,
    tenantId: auth.tenantId,
    email: profile?.email ?? null,
    name: profile?.name ?? null,
    isAdmin: profile?.isAdmin ?? false,
    workRoles: profile?.workRoles ?? [],
    login,
    loginWithSso,
    loginWithOktaCallback,
    hydrate,
    logout,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error?.message ?? null,
    ssoError,
  };

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
